import axios from 'axios';

// Always use relative URL so it works on any domain (preview, deployed, custom)
const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add Authorization header from localStorage as fallback when cookies don't work
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh token on 401 responses
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const headers = {};
        if (refreshToken) {
          headers['Authorization'] = `Bearer ${refreshToken}`;
        }
        await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true, headers });
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// User APIs
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const updateUser = (id, data) => api.patch(`/users/${id}`, data);

// Driver APIs
export const getDrivers = () => api.get('/drivers');
export const createDriver = (data) => api.post('/drivers', data);
export const getDriver = (id) => api.get(`/drivers/${id}`);
export const updateDriver = (id, data) => api.patch(`/drivers/${id}`, data);

// Record APIs
export const createRecord = (data) => api.post('/records', data);
export const getRecords = (params) => api.get('/records', { params });
export const getRecord = (id) => api.get(`/records/${id}`);
export const updateRecord = (id, data) => api.patch(`/records/${id}`, data);
export const deleteRecord = (id) => api.delete(`/records/${id}`);
export const allocateDriver = (id, driverId) => api.post(`/records/${id}/allocate`, { driver_id: driverId });
export const openRecord = (id) => api.post(`/records/${id}/open`);
export const submitSignature = (id, data) => api.post(`/records/${id}/submit-signature`, data);
export const resendEmail = (id) => api.post(`/records/${id}/resend-email`);
export const getAuditTrail = (id) => api.get(`/records/${id}/audit-trail`);
export const getMyJobs = () => api.get('/records/driver/my-jobs');
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getArchive = (params) => api.get('/archive', { params });

// File APIs
export const uploadPDF = (recordId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/files/upload-pdf?record_id=${recordId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadSignature = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/files/upload-signature', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadPhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/files/upload-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getFileUrl = (path) => {
  // Convert /uploads/pdfs/abc.pdf → /api/files/serve/pdfs/abc.pdf
  if (path && path.startsWith('/uploads/')) {
    const subPath = path.replace('/uploads/', '');
    return `/api/files/serve/${subPath}`;
  }
  return path;
};

export default api;
